import { createShortMarketSource, type RoundFetch, type ShortMarketSource } from './api';
import type { Round } from './model';
import { indexRounds, isShortRoundMarket, normaliseEventId, type RoundRef } from './round-index';
import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

/** Query key of rounds.json. The 5-minute page also writes each of its poll results here. */
export const SHORT_ROUNDS_FEED_KEY = ['ShortRoundsFeed'];
/** The event gate waits at most this long for rounds.json, then opens the event page. */
export const FEED_TIMEOUT_MS = 4_000;

const configuredSource = (): ShortMarketSource | undefined => {
  const base = import.meta.env.VITE_SHORT_MARKETS_API_PATH;
  try {
    return base ? createShortMarketSource(base) : undefined;
  } catch (error) {
    // Not shown to users: a mistyped tenant path turns the 5-minute pages and the home card off.
    console.warn('short-markets: VITE_SHORT_MARKETS_API_PATH is not a same-origin path; feature off', base, error);
    return undefined;
  }
};
/** The tenant's static short-market feed; undefined when VITE_SHORT_MARKETS_API_PATH is unset or invalid. */
export const shortMarketSource = configuredSource();

export type ShortRoundsFeed = {
  /** Last good rounds.json; kept when a later fetch fails. */
  feed?: RoundFetch;
  /** Local time (ms) at which `feed` arrived. */
  updatedAt: number;
  /** The first attempt is still in flight: nothing is known yet. A failed attempt settles it (no retry is waited for). */
  checking: boolean;
};
type FeedOptions = { refetchInterval?: UseQueryOptions<RoundFetch>['refetchInterval'] };

// Settles by FEED_TIMEOUT_MS whatever the fetch does: a request that ignores its abort signal must not
// hold the event gate's skeleton.
const fetchRounds = (source: ShortMarketSource, signal: AbortSignal) => {
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal.addEventListener('abort', abort);
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(() => {
      abort();
      reject(new Error(`Short-market feed timed out after ${FEED_TIMEOUT_MS}ms`));
    }, FEED_TIMEOUT_MS);
  });
  return Promise.race([source.rounds(controller.signal), timeout]).finally(() => {
    clearTimeout(timer);
    signal.removeEventListener('abort', abort);
  });
};

/**
 * Query options of rounds.json (key, fetcher, 30s stale, no retry): one definition for the hook and
 * for the boot prefetch (confs/boot-prefetch.ts), so both land on the same cache entry.
 */
export const shortRoundsFeedQueryOptions = (source: ShortMarketSource) => ({
  // eslint-disable-next-line @tanstack/query/exhaustive-deps -- one feed per tenant build: the key stays fixed (the 5-minute page writes to it too)
  queryKey: SHORT_ROUNDS_FEED_KEY,
  queryFn: ({ signal }: { signal: AbortSignal }) => fetchRounds(source, signal),
  staleTime: 30_000,
  // A failed feed means "no 5-minute rounds"; nothing retries it in a loop.
  retry: false as const,
});

const useFeedQuery = ({ refetchInterval }: FeedOptions = {}): ShortRoundsFeed => {
  const query = useQuery({ ...shortRoundsFeedQueryOptions(shortMarketSource!), refetchInterval });
  return {
    feed: query.data,
    updatedAt: query.dataUpdatedAt,
    // After a failure react-query reports the retry on mount as pending again (no data yet); a feed that
    // already failed is "no rounds" at once, so the event gate never waits for that retry.
    checking: query.status === 'pending' && query.errorUpdateCount === 0,
  };
};
const NO_FEED: ShortRoundsFeed = { updatedAt: 0, checking: false };
const useNoFeed = (): ShortRoundsFeed => NO_FEED;

/**
 * rounds.json through react-query (30s stale, no retry, 4s timeout), shared by the event gate,
 * the home card and the list filters. Without a configured feed there is no query and no request.
 */
export const useShortRoundsFeed: (options?: FeedOptions) => ShortRoundsFeed = shortMarketSource ? useFeedQuery : useNoFeed;

// Keyed on the rounds array: react-query keeps its identity while the rounds do not change.
const useRoundIndex = (rounds?: Round[]) => useMemo(() => indexRounds(rounds && { rounds }), [rounds]);

/**
 * The round and token of an event id: undefined while the first feed fetch is in flight, null when
 * the id is not a listed 5-minute round (no feed, failed feed, or not in rounds.json).
 */
export const useShortRoundMatch = (eventId: string): RoundRef | null | undefined => {
  const { feed, checking } = useShortRoundsFeed();
  const index = useRoundIndex(feed?.rounds);
  if (checking) return undefined;
  return index.get(normaliseEventId(eventId)) ?? null;
};

/** Stable list predicate: true for a market of a 5-minute round (see isShortRoundMarket). */
export const useIsShortRoundMarket = () => {
  const index = useRoundIndex(useShortRoundsFeed().feed?.rounds);
  return useCallback((market: Parameters<typeof isShortRoundMarket>[0]) => isShortRoundMarket(market, index), [index]);
};
